/**
 * Unit Conversion Utilities
 *
 * Provides conversion between SI (metric) and IP (imperial) units
 * commonly used in building energy simulation.
 */

/**
 * Unit system type
 */
export type UnitSystem = 'SI' | 'IP';

/**
 * Unit conversion definition
 */
interface UnitConversion {
  /** SI unit symbol */
  si: string;
  /** IP unit symbol */
  ip: string;
  /** Factor to multiply SI value to get IP value */
  factor: number;
  /** Optional offset for temperature-like conversions */
  offset?: number;
}

/**
 * Unit conversion definitions for common EnergyPlus units
 */
const UNIT_CONVERSIONS: Record<string, UnitConversion> = {
  // Temperature
  C: { si: 'C', ip: 'F', factor: 9 / 5, offset: 32 },

  // Energy
  J: { si: 'J', ip: 'Btu', factor: 0.000947817 },
  kJ: { si: 'kJ', ip: 'Btu', factor: 0.947817 },
  MJ: { si: 'MJ', ip: 'kBtu', factor: 0.947817 },
  GJ: { si: 'GJ', ip: 'MBtu', factor: 0.947817 },
  kWh: { si: 'kWh', ip: 'kBtu', factor: 3.412142 },
  Wh: { si: 'Wh', ip: 'Btu', factor: 3.412142 },

  // Power
  W: { si: 'W', ip: 'Btu/h', factor: 3.412142 },
  kW: { si: 'kW', ip: 'kBtu/h', factor: 3.412142 },
  MW: { si: 'MW', ip: 'MBtu/h', factor: 3.412142 },

  // Flow rate - air
  'm3/s': { si: 'm3/s', ip: 'cfm', factor: 2118.88 },
  'L/s': { si: 'L/s', ip: 'cfm', factor: 2.11888 },

  // Flow rate - water
  'kg/s': { si: 'kg/s', ip: 'lb/h', factor: 7936.64 },
  'm3/h': { si: 'm3/h', ip: 'gpm', factor: 4.40287 },

  // Length
  m: { si: 'm', ip: 'ft', factor: 3.28084 },
  cm: { si: 'cm', ip: 'in', factor: 0.393701 },
  mm: { si: 'mm', ip: 'in', factor: 0.0393701 },

  // Area
  m2: { si: 'm2', ip: 'ft2', factor: 10.7639 },

  // Volume
  m3: { si: 'm3', ip: 'ft3', factor: 35.3147 },

  // Pressure
  Pa: { si: 'Pa', ip: 'inH2O', factor: 0.00401865 },
  kPa: { si: 'kPa', ip: 'psi', factor: 0.145038 },

  // Heat transfer coefficient / U-value
  'W/m2-K': { si: 'W/m2-K', ip: 'Btu/h-ft2-F', factor: 0.176110 },

  // R-value
  'm2-K/W': { si: 'm2-K/W', ip: 'h-ft2-F/Btu', factor: 5.678263 },

  // Illuminance
  lux: { si: 'lux', ip: 'fc', factor: 0.092903 },

  // Mass flow (lb/s)
  'kg/s-lb': { si: 'kg/s', ip: 'lb/s', factor: 2.20462 },

  // Velocity
  'm/s': { si: 'm/s', ip: 'ft/min', factor: 196.85 },

  // Humidity
  'kg/kg': { si: 'kg/kg', ip: 'lb/lb', factor: 1 },

  // Density
  'kg/m3': { si: 'kg/m3', ip: 'lb/ft3', factor: 0.062428 },

  // Energy intensity
  'kWh/m2': { si: 'kWh/m2', ip: 'kBtu/ft2', factor: 0.316998 },
  'MJ/m2': { si: 'MJ/m2', ip: 'kBtu/ft2', factor: 0.088055 },
};

/**
 * Convert a value from SI to IP units
 */
export function convertToIP(value: number, unit: string): number {
  const conversion = UNIT_CONVERSIONS[unit];
  if (!conversion) return value;

  if (conversion.offset !== undefined) {
    return value * conversion.factor + conversion.offset;
  }
  return value * conversion.factor;
}

/**
 * Convert a value from IP to SI units
 */
export function convertToSI(value: number, unit: string): number {
  const conversion = UNIT_CONVERSIONS[unit];
  if (!conversion) return value;

  if (conversion.offset !== undefined) {
    return (value - conversion.offset) / conversion.factor;
  }
  return value / conversion.factor;
}

/**
 * Convert a value between unit systems
 */
export function convertValue(
  value: number,
  unit: string,
  targetSystem: UnitSystem
): number {
  if (targetSystem === 'IP') {
    return convertToIP(value, unit);
  }
  return value;
}

/**
 * Get the display unit for a given SI unit and target system
 */
export function getDisplayUnit(siUnit: string, targetSystem: UnitSystem): string {
  if (targetSystem === 'SI' || !siUnit) return siUnit;

  const conversion = UNIT_CONVERSIONS[siUnit];
  return conversion?.ip ?? siUnit;
}

/**
 * Check if a unit has a conversion available
 */
export function hasConversion(unit: string): boolean {
  return unit in UNIT_CONVERSIONS;
}

/**
 * Get all available SI units
 */
export function getAvailableSIUnits(): string[] {
  return Object.keys(UNIT_CONVERSIONS);
}

/**
 * Format a value with appropriate precision and unit
 */
export function formatValue(
  value: number,
  unit: string,
  targetSystem: UnitSystem,
  precision: number = 2
): string {
  const convertedValue = convertValue(value, unit, targetSystem);
  const displayUnit = getDisplayUnit(unit, targetSystem);

  // Use appropriate formatting based on value magnitude
  let formatted: string;
  if (Math.abs(convertedValue) >= 1000000) {
    formatted = (convertedValue / 1000000).toFixed(precision) + 'M';
  } else if (Math.abs(convertedValue) >= 1000) {
    formatted = (convertedValue / 1000).toFixed(precision) + 'k';
  } else if (Math.abs(convertedValue) < 0.01 && convertedValue !== 0) {
    formatted = convertedValue.toExponential(precision);
  } else {
    formatted = convertedValue.toFixed(precision);
  }

  return displayUnit ? `${formatted} ${displayUnit}` : formatted;
}

/**
 * Format a large number with commas
 */
export function formatNumber(value: number, precision: number = 0): string {
  return value.toLocaleString(undefined, {
    minimumFractionDigits: precision,
    maximumFractionDigits: precision,
  });
}

/**
 * Unit type categories for grouping similar units
 * Units in the same category can share an axis
 */
const UNIT_TYPE_CATEGORIES: Record<string, string> = {
  // Temperature
  C: 'temperature',
  K: 'temperature',
  F: 'temperature',

  // Energy
  J: 'energy',
  kJ: 'energy',
  MJ: 'energy',
  GJ: 'energy',
  kWh: 'energy',
  Wh: 'energy',
  Btu: 'energy',
  kBtu: 'energy',
  MBtu: 'energy',

  // Power
  W: 'power',
  kW: 'power',
  MW: 'power',
  'Btu/h': 'power',
  'kBtu/h': 'power',
  'MBtu/h': 'power',

  // Air flow rate
  'm3/s': 'air_flow',
  'L/s': 'air_flow',
  cfm: 'air_flow',

  // Water/mass flow rate
  'kg/s': 'mass_flow',
  'm3/h': 'water_flow',
  'lb/h': 'mass_flow',
  gpm: 'water_flow',
  'lb/s': 'mass_flow',

  // Length
  m: 'length',
  cm: 'length',
  mm: 'length',
  ft: 'length',
  in: 'length',

  // Area
  m2: 'area',
  ft2: 'area',

  // Volume
  m3: 'volume',
  ft3: 'volume',

  // Pressure
  Pa: 'pressure',
  kPa: 'pressure',
  inH2O: 'pressure',
  psi: 'pressure',

  // Heat transfer coefficient
  'W/m2-K': 'u_value',
  'Btu/h-ft2-F': 'u_value',

  // R-value
  'm2-K/W': 'r_value',
  'h-ft2-F/Btu': 'r_value',

  // Illuminance
  lux: 'illuminance',
  fc: 'illuminance',

  // Velocity
  'm/s': 'velocity',
  'ft/min': 'velocity',

  // Humidity
  'kg/kg': 'humidity',
  'lb/lb': 'humidity',

  // Density
  'kg/m3': 'density',
  'lb/ft3': 'density',

  // Energy intensity
  'kWh/m2': 'energy_intensity',
  'MJ/m2': 'energy_intensity',
  'kBtu/ft2': 'energy_intensity',

  // Dimensionless
  '': 'dimensionless',
  '-': 'dimensionless',
};

/**
 * Get the unit type category for a given unit
 * Units in the same category can share an axis
 */
export function getUnitCategory(unit: string): string {
  if (!unit || unit === '' || unit === '-') {
    return 'dimensionless';
  }
  return UNIT_TYPE_CATEGORIES[unit] ?? unit;
}

/**
 * Check if two units are compatible (same category)
 */
export function areUnitsCompatible(unit1: string, unit2: string): boolean {
  return getUnitCategory(unit1) === getUnitCategory(unit2);
}

/**
 * Get unique unit categories from a list of units
 */
export function getUniqueUnitCategories(units: string[]): string[] {
  const categories = new Set<string>();
  for (const unit of units) {
    categories.add(getUnitCategory(unit));
  }
  return Array.from(categories);
}
